/**
 * A BEÁLLÍTÁSOK LAP — a #25 váza.
 *
 * ═══ MIÉRT NEM SÉRTI A FELÜLET-SZABÁLYT ═══
 *
 * A `CLAUDE.md` §2 szerint NEGYEDIK FELÜLET NINCS. Ez a lap nem nyit újat:
 * nem kerül a *Hiánylista / Munkalista / Tár* fülsorba, hanem a fejlécben,
 * a Kilépés mellől nyílik — ADMINISZTRÁCIÓ, nem napi munka. A három
 * munkaképernyő marad három, és nem is nyitható meg innen: amíg a lap áll,
 * a fülsáv HELYÉN a visszaút áll.
 *
 * ⚠️ A FORMA DÖNTÉS VOLT, NEM ADOTTSÁG (Viktor, 2026-09-11, artboardon
 * eldöntve — `docs/tervezes/design/beallitasok/`). A másik jelölt egy nagy
 * ABLAK volt a munka fölött, a #24 NAV-kulcsablakának mintájára. Ez nyert,
 * mert négy szelet táblázatai nem férnek el ablakban: a cégnél már a hetedik
 * oszlop kiszorulna, és a megőrzés meg a türelmi idő csak egy MÁSODIK
 * ablakban látszana — ablak az ablakban.
 *
 * ⚠️ AMIT EZÉRT FIZETÜNK, ÉS AMI EZÉRT ITT VAN: a három munkafül eltűnik,
 * tehát a „hol vagyok" kérdésre egy pillanatra nincs válasz a fülsávban.
 * Ezért van a visszaút MEGNEVEZVE („Vissza a munkához"), nem egy × jel, és
 * ezért van `Escape`-re kötve.
 *
 * ═══ CSAK `ADMIN` — ÉS A HÍVÓ DÖNTI EL, NEM EZ A KOMPONENS ═══
 *
 * A belépő gomb csak `ADMIN`-nak látszik (`App.tsx`), a végpontok pedig
 * `@Roles('ADMIN')` mögött állnak. Ez a komponens nem ellenőriz újra: a
 * felületi rejtés kényelem, a kikényszerítés a szerveré (2.6/23 mintája). Ha
 * mégis idejut valaki, a `forbidden` ág kimondja.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Shortcut } from '@/hooks/useShortcuts';
import { CompanySettings } from './CompanySettings';
import { DocTypeSettings } from './DocTypeSettings';
import { NavSettings } from './NavSettings';
import { UserSettings } from './UserSettings';
import type { LayerRegistrar } from './useSettingsLayer';

/** A négy szelet. A `CLAUDE.md` szerinti sorrend: ami a leggyakoribb, elöl. */
const SLICES = ['companies', 'users', 'docTypes', 'nav'] as const;
type Slice = (typeof SLICES)[number];

export interface SettingsPageProps {
  onClose: () => void;
  /** A nyitott szelet ide jelzi az űrlapját vagy az ablakát — `useSettingsLayer`. */
  onLayer: LayerRegistrar;
}

export function SettingsPage({ onClose, onLayer }: SettingsPageProps) {
  const { t } = useTranslation();
  const [slice, setSlice] = useState<Slice>('companies');

  return (
    <>
      {/* A FÜLSÁV HELYE. Ugyanaz a `<nav>`, ugyanaz a magasság — de a három
          munkafül helyett EGY visszaút áll benne. A képernyő nem ugrik meg,
          és a szem ugyanott keresi a navigációt, ahol eddig. */}
      <nav aria-label={t('settings.title')}>
        <button type="button" className="tab tab--back" onClick={onClose}>
          ← {t('settings.back')}
          <kbd className="tab__kbd">Esc</kbd>
        </button>
      </nav>

      <main>
        <div className="settings__head">
          <div>
            <h1>{t('settings.title')}</h1>
            <p className="subtitle">{t('settings.subtitle')}</p>
          </div>
          {/* NEM DÍSZ: a lap egésze ADMIN-hoz kötött, és aki ide belép, annak
              tudnia kell, hogy amit itt állít, az MINDENKIRE hat. */}
          <span className="badge">{t('settings.adminOnly')}</span>
        </div>

        {/* A NÉGY SZELET — és ez NEM a felület-szabály megkerülése négy új
            képernyővel. Mind a négy UGYANAZT az egy tengelyt járja be: az
            iroda törzsadatát. Külön lapra tenni őket négy majdnem üres
            képernyőt adna. */}
        <div className="settings__slices" role="tablist" aria-label={t('settings.title')}>
          {SLICES.map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={slice === name}
              className={slice === name ? 'settings__slice active' : 'settings__slice'}
              onClick={() => setSlice(name)}
            >
              {t(`settings.tab.${name}`)}
            </button>
          ))}
        </div>

        {slice === 'companies' ? (
          <CompanySettings onLayer={onLayer} />
        ) : slice === 'users' ? (
          <UserSettings onLayer={onLayer} />
        ) : slice === 'docTypes' ? (
          /* ⚠️ NEM KAP `onLayer`-t, és ez nem feledékenység: a mátrixnak nincs
             rétege — se űrlap, se megerősítő ablak. Az `Escape` így a LAPOT
             zárja, ami itt a helyes lépés. */
          <DocTypeSettings />
        ) : (
          /* A kulcsbevitel ITT, a lapon áll (#25/4), és rétege nincs — a
             paletta ablaka külön úton nyílik. Az `onLayer` a Tensoft-kártyáé
             (#54): a visszavonás megerősítése és a kiadott token doboza. */
          <NavSettings onLayer={onLayer} />
        )}
      </main>
    </>
  );
}

/**
 * A LAP RÖVIDÍTÉSEI — a váz regiszterébe.
 *
 * ⚠️ FÜGGVÉNY, NEM EGY MÁSODIK `useShortcuts` HÍVÁS A LAPON BELÜL. A
 * regiszter EGY (`App.tsx`): a súgó abból épül, és egy második hívás
 * kettéhasítaná — pont attól veszítené el az értékét, amiért van.
 *
 * Ma egy van: az `Escape` a visszaút. A `CLAUDE.md` szerint egy `Escape` EGY
 * LÉPÉS VISSZA — ezért az `onEscape` a hívóé: előbb a szelet nyitott rétegét
 * zárja (űrlap, megerősítő ablak — `useSettingsLayer`), és csak ha nincs, a lapot.
 *
 * ⚠️ NINCS `Ctrl+1`/`Ctrl+2` A LAPON. A váz azokat kiveszi a regiszterből,
 * amíg a lap áll — nem feledékenységből: a két felületváltó a fülsávra
 * mutatna, ami ilyenkor nem is látszik. Egy billentyű, ami láthatatlan
 * felületre visz, rosszabb, mint ha nem lenne.
 */
export function settingsShortcuts(onEscape: () => void): Shortcut[] {
  return [{ key: 'Escape', labelKey: 'settings.back', allowInInput: true, run: onEscape }];
}
